import { SITE_API_BASE_URL } from '@m/config/env';
import { apiFetch, getAuthToken, getDefaultAuthMethod } from '@m/utils/http';

import {
  VIOLATION_ACCEPT,
  VIOLATION_CLOSE,
  VIOLATION_DECLINE,
  VIOLATION_DISPUTE,
} from 'apps/admin/constants/ViolationActions';

import type { Option } from '@m/types';

export const ViolationService = {
  fetchViolations: (
    params: {
      endDate?: string; // YYYY-MM-DD
      extViolationId?: string;
      licensePlate?: string;
      page?: number;
      pageSize?: number;
      siteIds?: number[]; // remove this for the first phase
      startDate?: string; // YYYY-MM-DD
      violationStatus?: number;
    } = {},
  ) => apiFetch('/violation/admin/violations', { params }),

  getViolationCount: (
    params: {
      endDate?: string; // YYYY-MM-DD
      extViolationId?: string;
      licensePlate?: string;
      siteIds?: number[];
      startDate?: string; // YYYY-MM-DD
      violationStatus?: number;
    } = {},
  ) => apiFetch('/violation/admin/violations/count', { params }),

  fetchViolationDetails: ({ violationId }: { violationId: number }) =>
    apiFetch(`/violation/${violationId}`),

  adjustFine: ({ violationId, fineAmount }: { fineAmount: number; violationId: number }) =>
    apiFetch(`/v1/admin/violations/${violationId}/adjust-fine`, {
      method: 'PUT',
      body: {
        fineAmount,
      },
    }),

  updateLicensePlate: ({
    violationId,
    plateText,
    plateState,
  }: {
    plateState: Option;
    plateText: string;
    violationId: number;
  }) =>
    apiFetch(`/violation/${violationId}/admin/license-plate-correction`, {
      method: 'PUT',
      body: {
        text: plateText,
        state: plateState,
      },
    }),

  closeViolation: ({ violationId, reasonId }: { reasonId: number; violationId: number }) =>
    apiFetch(`/violation/${violationId}/admin/close`, {
      method: 'PUT',
      body: {
        reasonId,
        action: VIOLATION_CLOSE,
      },
    }),

  disputeViolation: ({ violationId }: { violationId: number }) =>
    apiFetch(`/violation/${violationId}/admin/action`, {
      method: 'PUT',
      body: VIOLATION_DISPUTE,
    }),

  acceptDispute: ({ violationId, reasonId }: { reasonId: number; violationId: number }) =>
    apiFetch(`/violation/${violationId}/admin/close`, {
      method: 'PUT',
      body: {
        reasonId,
        action: VIOLATION_ACCEPT,
      },
    }),

  rejectDispute: ({ violationId }: { violationId: number }) =>
    apiFetch(`/violation/${violationId}/admin/action`, {
      method: 'PUT',
      body: VIOLATION_DECLINE,
    }),

  skiptraceReportExport: (body: {
    endDate: number; // millis
    siteIds?: number[];
    startDate: number; // millis
  }) =>
    apiFetch('/violation/admin/violations/skiptrace/export', {
      method: 'POST',
      body,
    }),

  /**
   * apiFetch uses workerize internally. service workers might not support form data?
   * @param file
   */
  importSkiptraceReport: async ({ file }: { file: File }) => {
    const formData = new FormData();
    formData.append('violations', file);

    const headers: HeadersInit = {};
    const authMethod = getDefaultAuthMethod();
    if (authMethod === 'token') {
      const authToken = await getAuthToken();
      if (authToken) {
        headers.Authorization = authToken;
      }
    }

    return fetch(`${SITE_API_BASE_URL}/violation/admin/violations/skiptrace/import`, {
      credentials: authMethod === 'cookie' ? 'include' : undefined,
      method: 'PUT',
      headers,
      body: formData,
    }).then((res) => res.json());
  },

  mailMergeExport: () =>
    apiFetch('/violation/admin/violations/mail/export', {
      method: 'POST',
      body: {},
    }),

  fetchExportHistory: () => apiFetch('/violation/admin/violations/mail/export/history'),

  fetchViolationStatuses: () => apiFetch('/violation/statuses'),

  fetchViolationCloseReasons: () => apiFetch('/violation/close-reasons'),
};
